import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  IconButton,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CalendarToday as ExtendIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LockReset as LockResetIcon,
  ContentCopy as CopyIcon,
  RestartAlt as RestartIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  DeleteForever as DeleteIcon,
  CardMembership as WelcomeIcon,
} from '@mui/icons-material';
import { runsApi, accountsApi, extractErrorMessage } from '../services/api';
import { WelcomeCardDialog } from '../components/WelcomeCard';
import { WelcomePayload } from '../services/types';
import { roomsApi } from '../services/roomsService';
import { Run, VMOperationResult, PasswordResetResult, ParticipantLockError, ProvisionLockError } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/auth';
import { CostEstimationCard } from '../components/CostEstimationCard';
import { VMDetailsPanel } from '../components/VMDetailsPanel';
import { ShutdownEvidenceChip } from '../components/ShutdownEvidenceChip';
import { RunClassificationChip } from '../components/RunClassificationChip';
import { RunRuntimeRow } from '../components/RunRuntimeRow';
import { RunDiagnosticsPanel } from '../components/RunDiagnosticsPanel';
import { ProvisioningFailureAlert, isProvisionLockActive } from '../components/ProvisioningFailureAlert';
import { ParticipantAccessPanel } from '../components/ParticipantAccessPanel';
import { RunMaterialsPanel } from '../components/RunMaterialsPanel';
import { RunVerificationPanel } from '../components/RunVerificationPanel';
import { RunFastCleanupPanel } from '../components/RunFastCleanupPanel';
import { RunKeepRunningPanel } from '../components/RunKeepRunningPanel';
import { RunRdpAccessPanel } from '../components/RunRdpAccessPanel';
import { RunTutorAccessPanel } from '../components/RunTutorAccessPanel';
import { RunClassificationPanel } from '../components/RunClassificationPanel';
import { RunRetrospectiveDialog } from '../components/RunRetrospectiveDialog';
import { useApiQuery } from '../hooks/useApiQuery';
import ErrorMessage from '../components/ErrorMessage';
import { NC_636_TRAINING_FILES_ENABLED } from '../config/featureFlags';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}


interface ConfirmDialogState {
  open: boolean;
  action: 'start' | 'stop' | 'restart' | null;
}

interface VMResultsDialogState {
  open: boolean;
  results: VMOperationResult[];
  message: string;
  severity: 'success' | 'warning' | 'error';
}

interface PasswordResetDialogState {
  open: boolean;
  sam: string;
  name: string;
  loading: boolean;
  result: PasswordResetResult | null;
  countdown: number;
}

export default function RunDetailsPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { tutor } = useAuth();

  const { data: run, loading, error, refetch: fetchRun } = useApiQuery<Run>(
    () => runsApi.getById(runId!),
    [runId],
    { enabled: !!runId }
  );

  // NC-798: resolve the optional Schulungsraum name. Falls back to the raw
  // roomId when the room can't be loaded (unknown/deleted id, endpoint down) so
  // a missing room never crashes the page. rooms-details returns inactive rooms
  // too, so historical runs keep showing the room name after deactivation.
  const roomId = run?.roomId;
  const [roomName, setRoomName] = useState<string | null>(null);
  useEffect(() => {
    if (!roomId) {
      setRoomName(null);
      return;
    }
    let cancelled = false;
    setRoomName(roomId); // optimistic raw-id fallback until the name resolves
    roomsApi
      .getById(roomId)
      .then((room) => {
        if (!cancelled) setRoomName(room.name);
      })
      .catch(() => {
        if (!cancelled) setRoomName(roomId);
      });
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  const [actionLoading, setActionLoading] = useState(false);
  const [provisionLoading, setProvisionLoading] = useState(false);
  // NC-458: 0 = Übersicht (metadata/cost/participants), 1 = Diagnose (live Azure/AD/KV state).
  const [activeTab, setActiveTab] = useState(0);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [newExpireDate, setNewExpireDate] = useState('');

  // NC-530: admin-only soft-delete dialog. Confirmation requires the operator
  // to retype the runId — this is the only path that wipes RG + AD users + KV
  // secrets, so a single misclick on /runs/<id> shouldn't be enough.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [passwordVisible, setPasswordVisible] = useState<Record<string, boolean>>({});
  const [fetchedPasswords, setFetchedPasswords] = useState<Record<string, string>>({});
  const [passwordLoading, setPasswordLoading] = useState<Record<string, boolean>>({});
  // NC-1052: Welcome-Card dialog (single participant or bulk "Alle Karten").
  const [welcomeDialog, setWelcomeDialog] = useState<{
    open: boolean;
    loading: boolean;
    error: string | null;
    payloads: WelcomePayload[];
  }>({ open: false, loading: false, error: null, payloads: [] });
  const [resultsExpanded, setResultsExpanded] = useState(true);

  // Password reset dialog state
  const [resetDialog, setResetDialog] = useState<PasswordResetDialogState>({
    open: false,
    sam: '',
    name: '',
    loading: false,
    result: null,
    countdown: 30,
  });
  const resetCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Polling refs for VM action refresh
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingCountRef = useRef(0);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    action: null,
  });

  // VM Results dialog state
  const [vmResultsDialog, setVmResultsDialog] = useState<VMResultsDialogState>({
    open: false,
    results: [],
    message: '',
    severity: 'success',
  });

  // NC-519: Add/Remove Participant dialog state
  const [addParticipantDialog, setAddParticipantDialog] = useState({
    open: false,
    firstName: '',
    lastName: '',
    loading: false,
  });
  const [removeParticipantDialog, setRemoveParticipantDialog] = useState({
    open: false,
    sam: '',
    name: '',
    loading: false,
  });

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenConfirmDialog = (action: 'start' | 'stop' | 'restart') => {
    setConfirmDialog({ open: true, action });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.action) return;

    const action = confirmDialog.action;
    handleCloseConfirmDialog();

    try {
      setActionLoading(true);

      let response;
      if (action === 'start') {
        response = await runsApi.startVMs(run!.runId);
      } else if (action === 'restart') {
        response = await runsApi.restartVMs(run!.runId);
      } else {
        response = await runsApi.stopVMs(run!.runId);
      }

      const { results: vmControlResponse, message } = response;
      const vmResults = vmControlResponse; // VMOperationResult[] (already extracted from response.results)
      const successCount = vmResults.filter((r) => r.success).length;
      const failureCount = vmResults.filter((r) => !r.success).length;

      // Determine severity
      let severity: VMResultsDialogState['severity'] = 'success';
      if (failureCount === vmResults.length) {
        severity = 'error';
      } else if (failureCount > 0) {
        severity = 'warning';
      }

      // Show results dialog
      setVmResultsDialog({
        open: true,
        results: vmResults,
        message: message || `${successCount} VM(s) erfolgreich, ${failureCount} fehlgeschlagen`,
        severity,
      });

      // Refresh run data
      await fetchRun();

      // Start 10s polling to refresh VM power states (6 polls = 60s)
      pollingCountRef.current = 0;
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(() => {
        pollingCountRef.current++;
        fetchRun();
        if (pollingCountRef.current >= 6) {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
        }
      }, 10000);

      // Show success snackbar for full success only
      if (failureCount === 0) {
        const actionLabel =
          action === 'start' ? 'gestartet' : action === 'restart' ? 'neugestartet' : 'gestoppt';
        showSnackbar(
          `Alle ${successCount} VM(s) erfolgreich ${actionLabel}`,
          'success'
        );
      }
    } catch (err) {
      showSnackbar(`Fehler: ${err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten'}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!run || !newExpireDate) return;

    try {
      setActionLoading(true);
      await runsApi.extend(run.runId, newExpireDate);
      showSnackbar('Ablaufdatum erfolgreich verlängert', 'success');
      setExtendDialogOpen(false);
      await fetchRun();
    } catch (err) {
      showSnackbar(`Fehler: ${err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten'}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRun = async () => {
    if (!run || deleteConfirmText !== run.runId) return;

    try {
      setActionLoading(true);
      await runsApi.deleteRun(run.runId);
      setDeleteDialogOpen(false);
      // Send the operator back to /runs immediately — the run will keep
      // existing in Cosmos with status='deleted' for the audit trail, but
      // it's filtered out of the default list view (see queryRuns NC-459).
      navigate('/runs');
    } catch (err) {
      showSnackbar(
        `Fehler beim Löschen: ${extractErrorMessage(err)}`,
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const exportCredentials = () => {
    if (!run || !run.accounts) return;

    // Use fetched passwords if available, otherwise show masked value
    const csv = [
      ['FirstName', 'LastName', 'UPN', 'SAM', 'Password', 'Note'],
      ...run.accounts.map((acc) => [
        acc.firstName,
        acc.lastName,
        acc.upn,
        acc.sam,
        fetchedPasswords[acc.sam] || '••••••••',
        fetchedPasswords[acc.sam] ? '' : 'Password available in Portal',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credentials-${run.runId}.csv`;
    a.click();

    // Show snackbar with info
    if (run.accounts.some((acc) => !fetchedPasswords[acc.sam])) {
      showSnackbar(
        'Hinweis: Nicht alle Passwörter wurden exportiert. Bitte Passwörter im Portal anzeigen lassen.',
        'info'
      );
    }
  };

  // NC-1052: open the Welcome-Card dialog for one participant (fetches the
  // server-assembled payload incl. the KV password on this explicit action).
  const handleOpenWelcome = async (sam: string) => {
    if (!runId) return;
    setWelcomeDialog({ open: true, loading: true, error: null, payloads: [] });
    try {
      const payload = await runsApi.getWelcome(runId, sam);
      setWelcomeDialog({ open: true, loading: false, error: null, payloads: [payload] });
    } catch (err) {
      setWelcomeDialog({ open: true, loading: false, error: extractErrorMessage(err), payloads: [] });
    }
  };

  // NC-1052: bulk "Alle Karten" — fetch every participant's card. Individual
  // failures are skipped so one missing KV secret doesn't blank the whole batch.
  const handleOpenAllWelcome = async () => {
    if (!runId || !run?.accounts?.length) return;
    setWelcomeDialog({ open: true, loading: true, error: null, payloads: [] });
    try {
      const results = await Promise.all(
        run.accounts.map((a) => runsApi.getWelcome(runId, a.sam).catch(() => null))
      );
      const payloads = results.filter((p): p is WelcomePayload => p !== null);
      const error = payloads.length === 0 ? 'Keine Karten konnten erstellt werden (Zugangsdaten fehlen).' : null;
      setWelcomeDialog({ open: true, loading: false, error, payloads });
    } catch (err) {
      setWelcomeDialog({ open: true, loading: false, error: extractErrorMessage(err), payloads: [] });
    }
  };

  const handleCloseWelcome = () => setWelcomeDialog((prev) => ({ ...prev, open: false }));

  const handleCopyWelcome = (text: string) => {
    navigator.clipboard?.writeText(text);
    showSnackbar('Zugangsdaten in Zwischenablage kopiert', 'success');
  };

  const handleOpenResetDialog = (sam: string, name: string) => {
    setResetDialog({ open: true, sam, name, loading: false, result: null, countdown: 30 });
  };

  const handleCloseResetDialog = () => {
    if (resetCountdownRef.current) clearInterval(resetCountdownRef.current);
    setResetDialog({ open: false, sam: '', name: '', loading: false, result: null, countdown: 30 });
  };

  const handleConfirmReset = async () => {
    setResetDialog((prev) => ({ ...prev, loading: true }));
    try {
      const result = await accountsApi.resetPassword(runId!, resetDialog.sam);

      // Invalidate cached password since it has changed
      setFetchedPasswords((prev) => {
        const next = { ...prev };
        delete next[resetDialog.sam];
        return next;
      });
      setPasswordVisible((prev) => ({ ...prev, [resetDialog.sam]: false }));

      // Start 30s countdown
      let count = 30;
      setResetDialog((prev) => ({ ...prev, loading: false, result, countdown: count }));

      resetCountdownRef.current = setInterval(() => {
        count--;
        setResetDialog((prev) => ({ ...prev, countdown: count }));
        if (count <= 0) {
          clearInterval(resetCountdownRef.current!);
          resetCountdownRef.current = null;
          setResetDialog((prev) => ({ ...prev, open: false }));
        }
      }, 1000);
    } catch (err) {
      setResetDialog((prev) => ({ ...prev, loading: false }));
      showSnackbar(`Fehler beim Zurücksetzen: ${err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten'}`, 'error');
    }
  };

  const handleCopyResetPassword = () => {
    if (resetDialog.result?.password) {
      navigator.clipboard.writeText(resetDialog.result.password);
      showSnackbar('Passwort in Zwischenablage kopiert', 'success');
    }
  };

  const togglePasswordVisibility = async (sam: string) => {
    // If already visible, just hide it
    if (passwordVisible[sam]) {
      setPasswordVisible((prev) => ({ ...prev, [sam]: false }));
      return;
    }

    // If not fetched yet, fetch from KeyVault API
    if (!fetchedPasswords[sam]) {
      try {
        setPasswordLoading((prev) => ({ ...prev, [sam]: true }));
        const data = await accountsApi.getPassword(runId!, sam);
        setFetchedPasswords((prev) => ({ ...prev, [sam]: data.password }));
        setPasswordVisible((prev) => ({ ...prev, [sam]: true }));
      } catch (err) {
        // NC-531: extractErrorMessage prefers the BE-provided body (e.g. the 404
        // "Passwort fuer Account ... nicht in KeyVault" or the 500 "Passwort
        // konnte nicht aus KeyVault geladen werden ... Admin kontaktieren") over
        // axios's "Request failed with status code N", which was what users saw.
        showSnackbar(`Fehler beim Laden des Passworts: ${extractErrorMessage(err)}`, 'error');
      } finally {
        setPasswordLoading((prev) => ({ ...prev, [sam]: false }));
      }
    } else {
      // Already fetched, just show it
      setPasswordVisible((prev) => ({ ...prev, [sam]: true }));
    }
  };

  // NC-519: 5-minute soft lock window matching BE (runs-participants-add/delete).
  // Buttons are visually disabled while a previous dispatch is still settling so
  // operators don't trigger guaranteed 409s — BE remains the source of truth.
  const PARTICIPANT_LOCK_MS = 5 * 60 * 1000;
  const participantLockSecondsAgo = (() => {
    const ts = run?.lastParticipantUpdateUtc;
    if (!ts) return null;
    const lastMs = Date.parse(ts);
    if (!Number.isFinite(lastMs)) return null;
    const delta = Date.now() - lastMs;
    return delta < PARTICIPANT_LOCK_MS ? Math.max(1, Math.floor(delta / 1000)) : null;
  })();
  const isParticipantLocked = participantLockSecondsAgo !== null;
  // NC-715: planned runs accept participants too — manifest-only path, no workflow dispatch.
  const canModifyParticipants =
    run?.status === 'planned' || run?.status === 'active' || run?.status === 'stopped';

  // 12 * 10s = ~120s — covers the typical ~30s GitHub workflow dispatch plus
  // a margin for AD apply / Cosmos write-back so the new account appears.
  const startParticipantPolling = () => {
    pollingCountRef.current = 0;
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      pollingCountRef.current++;
      fetchRun();
      if (pollingCountRef.current >= 12) {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
      }
    }, 10000);
  };

  // NC-778 (D6): manual provision start / retry. The BE enforces the 30-min
  // dispatch lock (409) — the button mirrors it via provisioningStartedUtc,
  // but a parallel trigger can still race us into the 409 path here.
  const handleProvision = async () => {
    if (!run) return;
    setProvisionLoading(true);
    try {
      const result = await runsApi.provision(run.runId);
      // NC-1627: the API owns the duration estimate (it knows the participant
      // count); the fallback stays neutral instead of naming minutes.
      showSnackbar(result.message || 'Provisionierung gestartet', 'success');
      await fetchRun();
    } catch (err) {
      const lockErr = err as ProvisionLockError;
      if (lockErr?.status === 409) {
        showSnackbar(lockErr.message || 'Provisionierung läuft bereits.', 'warning');
        await fetchRun();
      } else {
        showSnackbar(
          `Fehler: ${err instanceof Error ? err.message : 'Provisionierung konnte nicht gestartet werden'}`,
          'error'
        );
      }
    } finally {
      setProvisionLoading(false);
    }
  };

  const handleOpenAddParticipantDialog = () => {
    setAddParticipantDialog({ open: true, firstName: '', lastName: '', loading: false });
  };
  const handleCloseAddParticipantDialog = () => {
    setAddParticipantDialog((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmAddParticipant = async () => {
    if (!run) return;
    const firstName = addParticipantDialog.firstName.trim();
    const lastName = addParticipantDialog.lastName.trim();
    if (!firstName || !lastName) {
      showSnackbar('Vor- und Nachname sind erforderlich.', 'warning');
      return;
    }
    setAddParticipantDialog((prev) => ({ ...prev, loading: true }));
    // NC-715: planned-path is synchronous (manifest only) — no workflow to poll for.
    const wasPlanned = run.status === 'planned';
    try {
      const result = await runsApi.addParticipant(run.runId, { firstName, lastName });
      showSnackbar(result.message || 'Teilnehmer wird hinzugefügt — ca. 30s.', 'success');
      setAddParticipantDialog({ open: false, firstName: '', lastName: '', loading: false });
      await fetchRun();
      if (!wasPlanned) startParticipantPolling();
    } catch (err) {
      const lockErr = err as ParticipantLockError;
      if (lockErr?.status === 409 && lockErr.triggeredAtUtc) {
        const ago = Math.max(1, Math.floor((Date.now() - Date.parse(lockErr.triggeredAtUtc)) / 1000));
        showSnackbar(`Teilnehmer-Update läuft bereits (vor ${ago}s getriggert).`, 'warning');
      } else {
        showSnackbar(
          `Fehler: ${err instanceof Error ? err.message : 'Hinzufügen fehlgeschlagen'}`,
          'error'
        );
      }
      setAddParticipantDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleOpenRemoveParticipantDialog = (sam: string, name: string) => {
    setRemoveParticipantDialog({ open: true, sam, name, loading: false });
  };
  const handleCloseRemoveParticipantDialog = () => {
    setRemoveParticipantDialog((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmRemoveParticipant = async () => {
    if (!run || !removeParticipantDialog.sam) return;
    setRemoveParticipantDialog((prev) => ({ ...prev, loading: true }));
    // NC-715: planned-path is synchronous (manifest only) — no workflow to poll for.
    const wasPlanned = run.status === 'planned';
    try {
      const result = await runsApi.removeParticipant(run.runId, removeParticipantDialog.sam);
      showSnackbar(result.message || 'Teilnehmer wird entfernt — ca. 30s.', 'success');
      setRemoveParticipantDialog({ open: false, sam: '', name: '', loading: false });
      await fetchRun();
      if (!wasPlanned) startParticipantPolling();
    } catch (err) {
      const lockErr = err as ParticipantLockError;
      if (lockErr?.status === 409 && lockErr.triggeredAtUtc) {
        const ago = Math.max(1, Math.floor((Date.now() - Date.parse(lockErr.triggeredAtUtc)) / 1000));
        showSnackbar(`Teilnehmer-Update läuft bereits (vor ${ago}s getriggert).`, 'warning');
      } else {
        showSnackbar(
          `Fehler: ${err instanceof Error ? err.message : 'Entfernen fehlgeschlagen'}`,
          'error'
        );
      }
      setRemoveParticipantDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !run) {
    return (
      <Box>
        <ErrorMessage
          message={error || 'Lauf nicht gefunden'}
          title="Run-Details konnten nicht geladen werden"
          onRetry={fetchRun}
        />
        <Button startIcon={<BackIcon />} onClick={() => navigate('/runs')} sx={{ mt: 2, ml: 2 }}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  const canOperate = tutor && hasRole(tutor.role, 'operator');
  const canDelete = tutor && hasRole(tutor.role, 'admin');

  // NC-778 (D6) / NC-1576: dispatch lock — active while the last trigger is
  // younger than the BE lock window and has not failed yet.
  const provisionLockActive = isProvisionLockActive(run);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/runs')}>
            Zurück
          </Button>
          <Typography variant="h4">{run.runId}</Typography>
          <Chip
            label={run.workload.toUpperCase()}
            color={run.workload === 'cam' ? 'primary' : 'secondary'}
          />
          <Chip
            label={run.status || 'active'}
            color={run.status === 'active' ? 'success' : 'warning'}
          />
          {/* NC-1289: "wurde abgeschaltet?" answered in the header instead of
              three clicks deep in the Diagnostics tab. */}
          <ShutdownEvidenceChip run={run} />
          {/* NC-1288: always stated on the detail page, unlike the list. */}
          <RunClassificationChip classification={run.classification} alwaysShow />
        </Box>
        <Box display="flex" gap={1}>
          {canOperate && run.status === 'provisioning' && (
            <Tooltip
              title={
                provisionLockActive
                  ? 'Provisionierung läuft bereits — erneuter Start erst nach Ablauf der 30-Minuten-Sperre'
                  : 'Provisionierung für diese Schulung starten'
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={provisionLoading ? <CircularProgress size={20} /> : <StartIcon />}
                  onClick={handleProvision}
                  disabled={provisionLoading || provisionLockActive}
                >
                  Provisionierung starten
                </Button>
              </span>
            </Tooltip>
          )}
          {canOperate && (() => {
            // NC-431 follow-up: planned/provisioning runs have account rows but
            // no `vm` field — the backend rejects start/stop/restart with 400
            // "No VMs found for run". Disable the buttons and explain why
            // rather than letting the operator click into a guaranteed 400.
            const vmCount = run.accounts?.filter((acc) => acc.vm).length ?? 0;
            const noVmsTooltip =
              vmCount === 0
                ? 'Kein VM in diesem Run — Provisioning noch nicht abgeschlossen oder VMs bereits aufgeräumt'
                : null;
            return (
            <>
              <Tooltip title={noVmsTooltip ?? 'Alle VMs dieses Runs starten'}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={actionLoading ? <CircularProgress size={20} /> : <StartIcon />}
                    onClick={() => handleOpenConfirmDialog('start')}
                    disabled={actionLoading || run.status === 'active' || vmCount === 0}
                  >
                    VMs Starten
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={noVmsTooltip ?? 'Alle VMs stoppen und Compute-Kosten beenden'}>
                <span>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={actionLoading ? <CircularProgress size={20} /> : <StopIcon />}
                    onClick={() => handleOpenConfirmDialog('stop')}
                    disabled={actionLoading || run.status === 'stopped' || vmCount === 0}
                  >
                    VMs Stoppen
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={noVmsTooltip ?? 'Alle VMs neu starten (Betriebssystem-Neustart)'}>
                <span>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={actionLoading ? <CircularProgress size={20} /> : <RestartIcon />}
                    onClick={() => handleOpenConfirmDialog('restart')}
                    disabled={actionLoading || vmCount === 0}
                  >
                    VMs Neustarten
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Ablaufdatum des Runs verlängern">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ExtendIcon />}
                    onClick={() => setExtendDialogOpen(true)}
                    disabled={actionLoading}
                  >
                    Verlängern
                  </Button>
                </span>
              </Tooltip>
            </>
            );
          })()}
          {canDelete && run.status !== 'deleted' && (
            <Tooltip title="Schulungslauf löschen (Ressourcen, AD-User, KV-Secrets entfernen)">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setDeleteConfirmText('');
                    setDeleteDialogOpen(true);
                  }}
                  disabled={actionLoading}
                >
                  Löschen
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* NC-778 (D6): provisioning surface — failure alert + running job deep-link.
          NC-1236 (V3): make the failure state loud for the tutor — always show a
          reason (or a pointer when none is persisted yet), a retry affordance and
          a jump to the Diagnose tab where the live provisioning log is polled.
          NC-1576: short tutor summary by default, raw text behind "Technische Details". */}
      {(run.status === 'error' || run.provisioningError) && (
        <ProvisioningFailureAlert
          run={run}
          canOperate={Boolean(canOperate)}
          provisionLoading={provisionLoading}
          provisionLockActive={provisionLockActive}
          onRetry={handleProvision}
          onOpenDiagnostics={() => setActiveTab(1)}
        />
      )}
      {run.status === 'provisioning' && run.provisioningJobUrl && !run.provisioningError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Provisionierung wurde gestartet —{' '}
          <Link href={run.provisioningJobUrl} target="_blank" rel="noopener">
            GitHub-Actions-Job ansehen
          </Link>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Übersicht" value={0} />
        <Tab label="Diagnose" value={1} />
      </Tabs>

      {activeTab === 0 && (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadaten
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Kurs
                  </Typography>
                  <Typography variant="body1">{run.course}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Subscription
                  </Typography>
                  <Typography variant="body1">{run.subscription}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Resource Group
                  </Typography>
                  <Typography variant="body1">{run.runResourceGroup}</Typography>
                  {(run.status === 'planned' || run.status === 'provisioning') && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic', display: 'block' }}
                    >
                      (geplant — wird beim Provisioning erstellt)
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    OU DN
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                    {run.ouDn}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Beginnt
                  </Typography>
                  <Typography variant="body1">
                    {run.scheduledStart
                      ? formatDateSafe(run.scheduledStart)
                      : formatDateSafe(run.createdAtUtc, 'dd.MM.yyyy HH:mm')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Läuft ab
                  </Typography>
                  <Typography variant="body1">
                    {formatDateSafe(run.expireOn)}
                  </Typography>
                </Grid>
                {run.roomId && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Raum
                    </Typography>
                    <Typography variant="body1">{roomName ?? run.roomId}</Typography>
                  </Grid>
                )}
                {/* NC-1294 Slice 1: server-computed runtime. Renders nothing for a
                    legacy run with no usable start anchor. */}
                {run.runtime && run.runtime.state !== 'unknown' && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Laufzeit
                    </Typography>
                    <RunRuntimeRow runtime={run.runtime} />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Cost Estimation Card */}
        <Grid item xs={12} md={6}>
          <CostEstimationCard runId={run.runId} />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Teilnehmer ({run.accounts?.length || 0})
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {canOperate && (
                    <Tooltip
                      title={
                        !canModifyParticipants
                          ? `Teilnehmer können nur bei Status 'planned', 'active' oder 'stopped' hinzugefügt werden (aktuell: ${run.status ?? 'unbekannt'}).`
                          : isParticipantLocked
                            ? `Teilnehmer-Update läuft bereits (vor ${participantLockSecondsAgo}s getriggert) — bitte warten.`
                            : run.status === 'planned'
                              ? 'Teilnehmer dem Plan hinzufügen (wird beim Provisioning erstellt)'
                              : 'Neuen Teilnehmer in diesen Run aufnehmen'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={handleOpenAddParticipantDialog}
                          disabled={actionLoading || !canModifyParticipants || isParticipantLocked}
                        >
                          Teilnehmer hinzufügen
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {/*
                    NC-1402: the collective print affordance used to disappear silently
                    when a run had no accounts yet, so a tutor looking before provisioning
                    saw no trace of it and had no way to learn it exists. Its neighbour in
                    this very toolbar ("Teilnehmer hinzufügen") stays visible and disabled
                    with a tooltip that says why — we follow that convention here.
                  */}
                  {canOperate && (
                    <Tooltip
                      title={
                        (run.accounts?.length ?? 0) > 0
                          ? 'Willkommens-Karten für alle Teilnehmer erzeugen (drucken/kopieren)'
                          : 'Noch keine Teilnehmer-Accounts angelegt — Karten stehen nach dem Provisioning bereit.'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          startIcon={<WelcomeIcon />}
                          onClick={handleOpenAllWelcome}
                          disabled={(run.accounts?.length ?? 0) === 0}
                        >
                          Alle Karten
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Zugangsdaten als CSV-Datei herunterladen">
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={exportCredentials}
                    >
                      Als CSV exportieren
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {/* NC-1271: the workstation the participant is assigned to. */}
                      <TableCell align="center">Arbeitsplatz</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>UPN</TableCell>
                      <TableCell>SAM</TableCell>
                      <TableCell>Passwort</TableCell>
                      <TableCell>VM</TableCell>
                      <TableCell>IP</TableCell>
                      {canOperate && <TableCell align="right">Aktion</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {run.accounts?.map((account) => (
                      <TableRow key={account.sam}>
                        {/* NC-1271: a run from before the feature has no seat —
                            show that plainly instead of falling back to the row
                            index, which would look like a number that moves when
                            someone is removed. */}
                        <TableCell align="center">
                          {typeof account.seat === 'number' ? account.seat : '—'}
                        </TableCell>
                        <TableCell>
                          {account.firstName} {account.lastName}
                        </TableCell>
                        <TableCell>{account.upn}</TableCell>
                        <TableCell>{account.sam}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {passwordLoading[account.sam] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {passwordVisible[account.sam]
                                  ? (fetchedPasswords[account.sam] || account.password || 'N/A')
                                  : '••••••••'}
                              </Typography>
                            )}
                            <Tooltip title={passwordVisible[account.sam] ? 'Passwort verbergen' : 'Passwort anzeigen'}>
                              <IconButton
                                size="small"
                                onClick={() => togglePasswordVisibility(account.sam)}
                                disabled={passwordLoading[account.sam]}
                              >
                                {passwordVisible[account.sam] ? <HideIcon /> : <ViewIcon />}
                              </IconButton>
                            </Tooltip>
                            {canOperate && (
                              <Tooltip title="Passwort zurücksetzen">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    handleOpenResetDialog(
                                      account.sam,
                                      `${account.firstName} ${account.lastName}`
                                    )
                                  }
                                >
                                  <LockResetIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{account.vm?.computerName || '-'}</TableCell>
                        <TableCell>
                          {/* NC-1171 (#1164-S1): show the Public IP distinguishably when the
                              Public-RDP path is active; always keep the internal address visible. */}
                          {account.vm?.publicIp?.trim() ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" component="span">
                                  {account.vm.publicIp}
                                </Typography>
                                <Chip label="öffentlich" size="small" color="info" sx={{ height: 18 }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                intern: {account.vm?.privateIp || '-'}
                              </Typography>
                            </Box>
                          ) : (
                            account.vm?.privateIp || '-'
                          )}
                        </TableCell>
                        {canOperate && (
                          <TableCell align="right">
                            <Tooltip title={`Willkommens-Karte für ${account.firstName} ${account.lastName} (Login + Passwort)`}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Willkommens-Karte ${account.firstName} ${account.lastName}`}
                                onClick={() => handleOpenWelcome(account.sam)}
                              >
                                <WelcomeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                !canModifyParticipants
                                  ? `Teilnehmer können nur bei Status 'planned', 'active' oder 'stopped' entfernt werden (aktuell: ${run.status ?? 'unbekannt'}).`
                                  : isParticipantLocked
                                    ? `Teilnehmer-Update läuft bereits (vor ${participantLockSecondsAgo}s getriggert) — bitte warten.`
                                    : run.status === 'planned'
                                      ? `${account.firstName} ${account.lastName} aus dem Plan entfernen (noch nicht erstellt)`
                                      : `${account.firstName} ${account.lastName} aus diesem Run entfernen`
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Teilnehmer ${account.firstName} ${account.lastName} entfernen`}
                                  onClick={() =>
                                    handleOpenRemoveParticipantDialog(
                                      account.sam,
                                      `${account.firstName} ${account.lastName}`
                                    )
                                  }
                                  disabled={actionLoading || !canModifyParticipants || isParticipantLocked}
                                >
                                  <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* VM Details Panels */}
              {run.accounts && run.accounts.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    VM Details
                  </Typography>
                  {run.accounts
                    .filter((account) => account.vm?.vmName)
                    .map((account) => (
                      <Box key={account.vm!.vmName} sx={{ mb: 1 }}>
                        <VMDetailsPanel vmName={account.vm!.vmName} />
                      </Box>
                    ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* NC-1098 (Feat 2.0): run-scoped Schulungsunterlagen + Ad-hoc-Verteilung.
            Flag-gated (GO-LIVE via #1024) so it stays dark until the BE/infra
            prerequisites are live — same gate as the WorkloadEditor tab. */}
        {canOperate && NC_636_TRAINING_FILES_ENABLED && (
          <Grid item xs={12}>
            <RunMaterialsPanel
              runId={run.runId}
              workloadId={run.workloadId ?? run.workload}
              runStatus={run.status}
              materialsSyncRequestedUtc={run.materialsSyncRequestedUtc}
              materialsSyncJobUrl={run.materialsSyncJobUrl}
              canOperate={!!canOperate}
            />
          </Grid>
        )}

        {/* NC-1101 (Feat 2.0): Teilnehmer-Links + Projektdateien-Einsammlung */}
        {canOperate && (
          <Grid item xs={12}>
            <ParticipantAccessPanel runId={run.runId} runStatus={run.status} canOperate={!!canOperate} />
          </Grid>
        )}

        {/* NC-1235 (V2): "Lauf prüfen" + Abnahme-Ergebnis. Re-fetches the run
            after a dispatch so the 5-min lock chip and job link appear at once. */}
        {canOperate && (
          <Grid item xs={12}>
            <RunVerificationPanel
              runId={run.runId}
              runStatus={run.status}
              lastVerification={run.lastVerification}
              verifyRequestedUtc={run.verifyRequestedUtc}
              verifyJobUrl={run.verifyJobUrl}
              canOperate={!!canOperate}
              onDispatched={fetchRun}
            />
          </Grid>
        )}

        {/* NC-1168 (Slice 3, retro path): flip the fast-cleanup opt-in after
            creation. Warns when the run's end has already passed, because then
            the next sweep tears it down. */}
        <Grid item xs={12} md={6}>
          <RunFastCleanupPanel
            runId={run.runId}
            fastCleanup={run.fastCleanup}
            canOperate={!!canOperate}
            onChanged={fetchRun}
          />
        </Grid>

        {/* NC-1567: admin opt-out from the 19:00 evening shutdown. Operators
            see the state read-only; viewers do not get the card. */}
        {canOperate && (
          <Grid item xs={12} md={6}>
            <RunKeepRunningPanel
              runId={run.runId}
              keepRunning={run.keepRunning}
              canEdit={!!tutor && hasRole(tutor.role, 'admin')}
              onChanged={fetchRun}
            />
          </Grid>
        )}

        {/* NC-1579: edit the RDP source-IP whitelist after creation — the
            participants' IPs are often unknown when the run is created.
            Operators edit; viewers see the confirmed list read-only. */}
        <Grid item xs={12} md={6}>
          <RunRdpAccessPanel run={run} canEdit={!!canOperate} onChanged={fetchRun} />
        </Grid>

        {/* NC-1589: the run's tutor account — local admin on every trainee VM
            (installations), credentials via the audited Welcome Card. */}
        {canOperate && (
          <Grid item xs={12} md={6}>
            <RunTutorAccessPanel run={run} onShowCredentials={handleOpenWelcome} />
          </Grid>
        )}

        {/* NC-1288 (retro path): state Produktiv/Test after creation — the
            common case, since a run is usually only recognised as a test run
            once it is already up. Deliberately adjacent to the cleanup card:
            both are called "Testlauf" in everyday speech, and side by side each
            card can name what the other one is not. */}
        <Grid item xs={12} md={6}>
          <RunClassificationPanel
            runId={run.runId}
            classification={run.classification}
            canOperate={!!canOperate}
            onChanged={fetchRun}
          />
        </Grid>

        {/* NC-1281 S3: the Run-Rueckblick. Renders itself only once the run is
            over (expired/stopped) — asking "how did it go?" mid-run collects a
            guess, so the visibility rule lives in the component next to the
            statuses it names. */}
        <Grid item xs={12}>
          <RunRetrospectiveDialog
            runId={run.runId}
            workloadId={run.workloadId ?? run.workload}
            runStatus={run.status}
            canOperate={!!canOperate}
          />
        </Grid>
      </Grid>
      )}

      {activeTab === 1 && <RunDiagnosticsPanel runId={run.runId} />}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
        <DialogTitle>
          {confirmDialog.action === 'start'
            ? 'VMs Starten'
            : confirmDialog.action === 'restart'
              ? 'VMs Neustarten'
              : 'VMs Stoppen'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'start' ? (
              <>
                Möchten Sie alle VMs für diesen Run wirklich <strong>starten</strong>?
                <br />
                <br />
                Dies startet {run.accounts?.filter(acc => acc.vm).length || 0} VM(s).
              </>
            ) : confirmDialog.action === 'restart' ? (
              <>
                Möchten Sie alle VMs für diesen Run wirklich <strong>neustarten</strong>?
                <br />
                <br />
                Dies startet {run.accounts?.filter(acc => acc.vm).length || 0} VM(s) neu.
              </>
            ) : (
              <>
                Möchten Sie alle VMs für diesen Run wirklich <strong>stoppen und deallocieren</strong>?
                <br />
                <br />
                Dies stoppt {run.accounts?.filter(acc => acc.vm).length || 0} VM(s) und beendet die
                Compute-Kosten.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Abbrechen</Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={
              confirmDialog.action === 'start'
                ? 'success'
                : confirmDialog.action === 'restart'
                  ? 'info'
                  : 'warning'
            }
            autoFocus
          >
            {confirmDialog.action === 'start'
              ? 'Starten'
              : confirmDialog.action === 'restart'
                ? 'Neustarten'
                : 'Stoppen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Dialog */}
      <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)}>
        <DialogTitle>Ablaufdatum verlängern</DialogTitle>
        <DialogContent>
          <TextField
            label="Neues Ablaufdatum"
            type="date"
            fullWidth
            value={newExpireDate}
            onChange={(e) => setNewExpireDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleExtend} variant="contained" disabled={!newExpireDate || actionLoading}>
            Verlängern
          </Button>
        </DialogActions>
      </Dialog>

      {/* NC-530: Delete Run dialog — admin-only, retype RunId to confirm. */}
      <Dialog open={deleteDialogOpen} onClose={() => !actionLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Schulungslauf löschen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Dies entfernt die Ressourcen-Gruppe, alle AD-Benutzer und KeyVault-Secrets
            dieses Runs. Die Aktion ist <strong>nicht umkehrbar</strong>. Der Cosmos-Eintrag
            bleibt mit <code>status: 'deleted'</code> als Audit-Spur erhalten.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Zum Bestätigen RunId <strong>{run.runId}</strong> eintippen:
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={run.runId}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>Abbrechen</Button>
          <Button
            onClick={handleDeleteRun}
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== run.runId || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* VM Results Dialog */}
      <Dialog
        open={vmResultsDialog.open}
        onClose={() => setVmResultsDialog({ ...vmResultsDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {vmResultsDialog.severity === 'success' && <SuccessIcon color="success" />}
            {vmResultsDialog.severity === 'warning' && <WarningIcon color="warning" />}
            {vmResultsDialog.severity === 'error' && <ErrorIcon color="error" />}
            <Typography variant="h6">VM-Operation Ergebnisse</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity={vmResultsDialog.severity} sx={{ mb: 2 }}>
            {vmResultsDialog.message}
          </Alert>

          <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer', mb: 1 }}>
            <Typography variant="subtitle2" onClick={() => setResultsExpanded(!resultsExpanded)}>
              Details ({vmResultsDialog.results.length} VMs)
            </Typography>
            <IconButton size="small" onClick={() => setResultsExpanded(!resultsExpanded)}>
              {resultsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={resultsExpanded}>
            <List dense>
              {vmResultsDialog.results.map((result, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {result.success ? (
                      <SuccessIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.vmName}
                    secondary={result.success ? result.message : result.error}
                    secondaryTypographyProps={{
                      color: result.success ? 'text.secondary' : 'error',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVmResultsDialog({ ...vmResultsDialog, open: false })}
            variant="contained"
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialog.open} onClose={resetDialog.result ? handleCloseResetDialog : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>Passwort zurücksetzen — {resetDialog.name}</DialogTitle>
        <DialogContent>
          {!resetDialog.result ? (
            <DialogContentText>
              Soll das Passwort für <strong>{resetDialog.name}</strong> ({resetDialog.sam}) zurückgesetzt werden?
              <br /><br />
              Das neue Passwort wird einmalig angezeigt und kann kopiert werden.
              Nach 30 Sekunden wird dieses Fenster automatisch geschlossen.
            </DialogContentText>
          ) : (
            <Box>
              {resetDialog.result.adSyncPending && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Das Passwort wurde in KeyVault gespeichert. Die AD-Synchronisation ist noch ausstehend (abhängig von C1 – KeyVault-Migration).
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                Dieses Fenster schließt sich automatisch in <strong>{resetDialog.countdown}s</strong>.
              </Alert>
              <Typography variant="subtitle2" gutterBottom>Neues Passwort:</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: 'monospace', fontSize: '1.1rem', flexGrow: 1, letterSpacing: 1 }}
                >
                  {resetDialog.result.password}
                </Typography>
                <Tooltip title="In Zwischenablage kopieren">
                  <IconButton onClick={handleCopyResetPassword} size="small">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!resetDialog.result ? (
            <>
              <Button onClick={handleCloseResetDialog}>Abbrechen</Button>
              <Button
                onClick={handleConfirmReset}
                variant="contained"
                color="warning"
                disabled={resetDialog.loading}
              >
                {resetDialog.loading ? 'Wird zurückgesetzt…' : 'Zurücksetzen'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseResetDialog} variant="contained">Schließen</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* NC-519: Add Participant Dialog */}
      <Dialog
        open={addParticipantDialog.open}
        onClose={addParticipantDialog.loading ? undefined : handleCloseAddParticipantDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Teilnehmer hinzufügen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Der neue Teilnehmer wird per GitHub-Workflow im AD und KeyVault angelegt.
            Die Aktion dauert ca. 30 Sekunden. SAM und Passwort erscheinen automatisch in der Liste.
          </DialogContentText>
          <TextField
            label="Vorname"
            fullWidth
            autoFocus
            value={addParticipantDialog.firstName}
            onChange={(e) =>
              setAddParticipantDialog((prev) => ({ ...prev, firstName: e.target.value }))
            }
            disabled={addParticipantDialog.loading}
            sx={{ mb: 2 }}
            inputProps={{ 'aria-label': 'Vorname' }}
          />
          <TextField
            label="Nachname"
            fullWidth
            value={addParticipantDialog.lastName}
            onChange={(e) =>
              setAddParticipantDialog((prev) => ({ ...prev, lastName: e.target.value }))
            }
            disabled={addParticipantDialog.loading}
            inputProps={{ 'aria-label': 'Nachname' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddParticipantDialog}
            disabled={addParticipantDialog.loading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmAddParticipant}
            variant="contained"
            color="primary"
            disabled={
              addParticipantDialog.loading ||
              !addParticipantDialog.firstName.trim() ||
              !addParticipantDialog.lastName.trim()
            }
            startIcon={
              addParticipantDialog.loading ? <CircularProgress size={20} /> : <PersonAddIcon />
            }
          >
            {addParticipantDialog.loading ? 'Wird hinzugefügt…' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NC-519: Remove Participant Confirmation Dialog */}
      <Dialog
        open={removeParticipantDialog.open}
        onClose={removeParticipantDialog.loading ? undefined : handleCloseRemoveParticipantDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Teilnehmer entfernen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll <strong>{removeParticipantDialog.name}</strong> ({removeParticipantDialog.sam})
            wirklich aus diesem Run entfernt werden?
            <br />
            <br />
            Der AD-User wird gelöscht, das KeyVault-Secret entfernt und das Run-Manifest
            aktualisiert. VM-Cleanup ist nicht Teil dieser Aktion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRemoveParticipantDialog}
            disabled={removeParticipantDialog.loading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmRemoveParticipant}
            variant="contained"
            color="error"
            disabled={removeParticipantDialog.loading}
            startIcon={
              removeParticipantDialog.loading ? (
                <CircularProgress size={20} />
              ) : (
                <PersonRemoveIcon />
              )
            }
          >
            {removeParticipantDialog.loading ? 'Wird entfernt…' : 'Entfernen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NC-1052: Welcome-Card dialog (single or bulk) */}
      <WelcomeCardDialog
        open={welcomeDialog.open}
        loading={welcomeDialog.loading}
        error={welcomeDialog.error}
        payloads={welcomeDialog.payloads}
        onClose={handleCloseWelcome}
        onCopy={handleCopyWelcome}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
